import {useMemo} from 'react';
import {getHourMinutes, getStartOfDay} from '../../../library/dates';
import {TSlot, TTimetableItem} from '../../../redux/timetable/timetable.type';
import {byWeight} from './timetableSort';

// The slot's timeStart is the authoritative date for a lecture — l.time can be
// stale (e.g. after duplicate, only tsid is persisted, time stays 0).
export const getLectureTime = (l: TTimetableItem, slots: TSlot[]): number =>
    slots.find(s => s.tsid === l.tsid)?.timeStart ?? l.time;

type ConferenceDay = {label: string; timestamp: number};

export function useTimeGroups(lectures: TTimetableItem[], currentDay: ConferenceDay | undefined, slots: TSlot[]) {
    return useMemo(() => {
        const filtered = currentDay
            ? lectures.filter(l => getStartOfDay(getLectureTime(l, slots)) === currentDay.timestamp)
            : lectures;

        const lectureItems: TTimetableItem[] = [];
        const breakItems: TTimetableItem[] = [];
        for(const item of filtered) {
            (item.isBreak ? breakItems : lectureItems).push(item);
        }

        const groupsMap = new Map<string, {time: string; rawTime: number; tsid: number | null; lectures: TTimetableItem[]}>();
        for(const lecture of lectureItems) {
            const lectureTime = getLectureTime(lecture, slots);
            const key = `s:${lecture.tsid}`;
            const group = groupsMap.get(key) ?? {
                time: lectureTime ? getHourMinutes(lectureTime) : '',
                rawTime: lectureTime,
                tsid: lecture.tsid ?? null,
                lectures: [],
            };
            group.lectures.push(lecture);
            groupsMap.set(key, group);
        }
        const grouped = Array.from(groupsMap.values());
        grouped.forEach(g => g.lectures.sort(byWeight));

        const breakGroups = breakItems.map(b => {
            const breakTime = getLectureTime(b, slots);
            return {
                time: getHourMinutes(breakTime),
                rawTime: breakTime,
                isBreak: true as const,
                breakItem: b,
            };
        });

        return [...grouped, ...breakGroups].sort((a, b) => a.rawTime - b.rawTime);
    }, [lectures, currentDay, slots]);
}
